import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { clipboardText, mailtoLink } from '../userOffers/carrierInquiriesApi';

/**
 * Szkic wiadomości do klienta (prace 26.10.2026, pakiet P1): temat, treść,
 * adresat; „Kopiuj”, „Otwórz w poczcie” (`mailto:`) i „Wysłane ręcznie”.
 * **Z aplikacji nic nie wychodzi** — wzorzec zapytań do przewoźników
 * (`CarrierInquiries.tsx`, te same reguły odnośnika i schowka).
 */
export const SEND_NOTE =
  'Z aplikacji nic nie wychodzi: skopiuj treść albo otwórz ją w swojej poczcie, wyślij i oznacz „Wysłane ręcznie”.';

export interface MessageDraftProps {
  subject: string;
  body: string;
  recipient: string | null;
  /** Szkic do poprawy i wysłania; `false` — tylko podgląd (wysłane, zastąpione). */
  editable: boolean;
  busy?: boolean;
  /** Prefiks identyfikatorów elementów (`aviz-mail`, `client-update-12`…). */
  testIdPrefix: string;
  sentLabel?: string;
  onSave: (v: { subject: string; body: string; recipient: string }) => Promise<void>;
  onSent: () => Promise<void>;
}

export const MessageDraft = ({
  subject: initialSubject,
  body: initialBody,
  recipient: initialRecipient,
  editable,
  busy = false,
  testIdPrefix,
  sentLabel = 'Wysłane ręcznie',
  onSave,
  onSent,
}: MessageDraftProps) => {
  const notify = useNotify();
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [recipient, setRecipient] = useState(initialRecipient ?? '');
  useEffect(() => {
    setSubject(initialSubject);
    setBody(initialBody);
    setRecipient(initialRecipient ?? '');
  }, [initialSubject, initialBody, initialRecipient]);

  const dirty = subject !== initialSubject || body !== initialBody || recipient !== (initialRecipient ?? '');
  const mail = mailtoLink({ subject, body, contact: { email: recipient } });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(clipboardText({ subject, body }));
      notify('Skopiowano temat i treść', { type: 'info' });
    } catch {
      notify('Nie udało się skopiować — zaznacz treść i skopiuj ręcznie', { type: 'warning' });
    }
  };

  const copyForMail = async () => {
    if (mail.bodyIncluded) return;
    try {
      await navigator.clipboard.writeText(body);
      notify('Treść jest za długa dla programu pocztowego — skopiowano ją do schowka, wklej ją w wiadomość', {
        type: 'info',
      });
    } catch {
      notify('Treść jest za długa dla programu pocztowego — skopiuj ją przyciskiem „Kopiuj”', { type: 'warning' });
    }
  };

  return (
    <Stack spacing={1} data-testid={`${testIdPrefix}-draft`}>
      <TextField
        size="small"
        label="Adresat"
        value={recipient}
        disabled={!editable || busy}
        onChange={(e) => setRecipient(e.target.value)}
        slotProps={{ htmlInput: { maxLength: 320, 'data-testid': `${testIdPrefix}-recipient` } }}
      />
      <TextField
        size="small"
        label="Temat"
        value={subject}
        disabled={!editable || busy}
        onChange={(e) => setSubject(e.target.value)}
        slotProps={{ htmlInput: { maxLength: 300, 'data-testid': `${testIdPrefix}-subject` } }}
      />
      <TextField
        size="small"
        label="Treść"
        value={body}
        multiline
        minRows={6}
        disabled={!editable || busy}
        onChange={(e) => setBody(e.target.value)}
        slotProps={{ htmlInput: { maxLength: 8000, 'data-testid': `${testIdPrefix}-body` } }}
      />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" onClick={() => void copy()} data-testid={`${testIdPrefix}-copy`}>
          Kopiuj
        </Button>
        <Button
          size="small"
          variant="outlined"
          href={mail.href}
          onClick={() => void copyForMail()}
          data-testid={`${testIdPrefix}-mailto`}
        >
          Otwórz w poczcie
        </Button>
        {editable ? (
          <>
            <Button
              size="small"
              disabled={busy || !dirty || subject.trim() === '' || body.trim() === ''}
              onClick={() => void onSave({ subject, body, recipient })}
              data-testid={`${testIdPrefix}-save`}
            >
              Zapisz poprawki
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={busy || dirty}
              onClick={() => void onSent()}
              data-testid={`${testIdPrefix}-sent`}
            >
              {sentLabel}
            </Button>
          </>
        ) : null}
      </Box>
      {editable ? (
        <Typography variant="caption" color="text.secondary">
          {dirty ? 'Najpierw zapisz poprawki — oznaczasz wysłanie treści, którą widać w aplikacji.' : SEND_NOTE}
        </Typography>
      ) : null}
      {editable && !dirty ? (
        <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
          „{sentLabel}” to Twoje oświadczenie — zapisze się z Twoim imieniem i godziną.
        </Alert>
      ) : null}
    </Stack>
  );
};
